import React from 'react';
import { useViewportCamera, type Bounds } from '@/hooks/useViewportCamera';
import { ViewportControls } from './ViewportControls';

interface ViewportProps {
  children: React.ReactNode;
  entities?: Array<{ x: number; y: number; width: number; height: number }>;
}

export const Viewport = React.forwardRef<
  { fitToContent: (b: Bounds) => void; resetZoom: () => void },
  ViewportProps
>(({ children, entities = [] }, ref) => {
  const {
    containerRef, zoom, isDragging, handlers,
    fitToContent, resetZoom, getTransformStyle,
  } = useViewportCamera({ mode: 'transform' });

  React.useImperativeHandle(ref, () => ({ fitToContent, resetZoom }), [fitToContent, resetZoom]);

  const computeBounds = React.useCallback((): Bounds | null => {
    if (entities.length === 0) return null;
    return {
      minX: Math.min(...entities.map(e => e.x)),
      minY: Math.min(...entities.map(e => e.y)),
      maxX: Math.max(...entities.map(e => e.x + e.width)),
      maxY: Math.max(...entities.map(e => e.y + e.height)),
    };
  }, [entities]);

  const handleFit = React.useCallback(() => {
    const b = computeBounds();
    if (b) fitToContent(b);
  }, [computeBounds, fitToContent]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden select-none"
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      {...handlers}
    >
      <div style={getTransformStyle()} className="will-change-transform">
        {children}
      </div>
      <ViewportControls zoom={zoom} onReset={resetZoom} onFit={handleFit} />
    </div>
  );
});

Viewport.displayName = 'Viewport';
